import React from "react";
import styled from "styled-components";

const Page = styled.div`
  --bg: #07090e;
  --panel: #0d111a;
  --surface: #131824;
  --text: #f3f4f8;
  --dim: #9aa0b2;
  --faint: #636878;
  --accent: #00e599;
  --accent-glow: rgba(0, 229, 153, 0.18);
  --cyan: #06b6d4;
  --success: #10b981;
  --border: rgba(255, 255, 255, 0.08);

  min-height: 100vh;
  background: var(--bg);
  color: var(--text);
  font-family:
    -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial,
    sans-serif;
  line-height: 1.68;
  -webkit-font-smoothing: antialiased;

  * {
    box-sizing: border-box;
  }
`;

const Wrap = styled.div`
  max-width: 780px;
  margin: 0 auto;
  padding: 44px 22px 90px;
`;

const Header = styled.header`
  display: flex;
  align-items: center;
  gap: 16px;
  padding-bottom: 24px;
  margin-bottom: 30px;
  border-bottom: 1px solid var(--border);
`;

const Badge = styled.div`
  width: 52px;
  height: 52px;
  border-radius: 14px;
  background: linear-gradient(145deg, #00c47d, #00e599);
  display: flex;
  align-items: center;
  justify-content: center;
  flex: none;
  font-size: 26px;
  box-shadow: 0 8px 24px var(--accent-glow);
`;

const Brand = styled.div`
  font-size: 24px;
  font-weight: 800;
  letter-spacing: -0.5px;

  span {
    color: var(--accent);
  }
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: 800;
  letter-spacing: -1px;
  margin: 8px 0 6px;
  color: #ffffff;
`;

const Updated = styled.p`
  color: var(--faint);
  font-size: 14px;
  margin-bottom: 32px;
`;

const Section = styled.h2`
  font-size: 20px;
  font-weight: 700;
  margin: 38px 0 12px;
  color: #ffffff;
  display: flex;
  align-items: center;
  gap: 10px;

  &::before {
    content: "";
    width: 4px;
    height: 18px;
    background: var(--accent);
    border-radius: 2px;
    flex: none;
  }
`;

const Body = styled.div`
  p,
  li {
    color: var(--dim);
    font-size: 15.5px;
    line-height: 1.7;
    margin-bottom: 12px;
  }

  ul {
    padding-left: 20px;
    margin-bottom: 18px;
  }

  li {
    margin-bottom: 6px;
  }

  strong {
    color: var(--text);
  }

  a {
    color: var(--accent);
    text-decoration: none;
    transition: opacity 0.2s;

    &:hover {
      text-decoration: underline;
    }
  }

  code {
    background: rgba(255, 255, 255, 0.06);
    padding: 2px 6px;
    border-radius: 6px;
    font-size: 14px;
    color: #e2e8f0;
  }
`;

const Card = styled.div`
  background: var(--panel);
  border: 1px solid var(--border);
  border-radius: 18px;
  padding: 22px 24px;
  margin: 18px 0 24px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
`;

const HighlightBox = styled(Card)`
  background: linear-gradient(
    145deg,
    rgba(0, 229, 153, 0.08),
    rgba(6, 182, 212, 0.05)
  );
  border: 1px solid rgba(0, 229, 153, 0.25);
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin: 18px 0;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureBox = styled.div`
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: 14px;
  padding: 16px 18px;

  h4 {
    margin: 0 0 6px;
    font-size: 15px;
    font-weight: 700;
    color: var(--text);
    display: flex;
    align-items: center;
    gap: 8px;
  }

  p {
    margin: 0;
    font-size: 14px;
    color: var(--dim);
  }
`;

const Footer = styled.footer`
  margin-top: 60px;
  padding-top: 24px;
  border-top: 1px solid var(--border);
  color: var(--faint);
  font-size: 13.5px;
  text-align: center;
`;

const Expentry = () => {
  return (
    <Page>
      <Wrap>
        <Header>
          <Badge>💎</Badge>
          <div>
            <Brand>
              Expentry / <span>Track Your Expense</span>
            </Brand>
            <span style={{ fontSize: "13px", color: "var(--faint)" }}>
              Private Personal Finance &amp; Expense Cockpit
            </span>
          </div>
        </Header>

        <Title>Privacy Policy</Title>
        <Updated>Last updated: September 25, 2026</Updated>

        <Body>
          <HighlightBox>
            <p style={{ margin: 0, color: "#d1fae5", fontSize: "16px" }}>
              <strong>Summary &amp; Privacy Pledge:</strong> Expentry
              (&ldquo;Track Your Expense&rdquo;, Package:{" "}
              <code>com.zaynmiraj.Expentry</code>) is built on an absolute{" "}
              <strong>100% offline, privacy-first local architecture</strong>.
              Your financial records, incomes, expenses, budget categories,
              notes, and preferences remain exclusively on your physical device.
              We do not track, collect, upload, share, or sell your personal or
              financial data.
            </p>
          </HighlightBox>

          <Section>1. Core Architecture: 100% On-Device &amp; Private</Section>
          <p>
            Expentry is designed to give you complete mastery over your finances
            without ever compromising your personal confidentiality:
          </p>
          <ul>
            <li>
              <strong>No Cloud Databases:</strong> All transactions, financial
              amounts, timestamps, category tags, and personal notes are saved
              exclusively inside your device&apos;s sandboxed local storage (
              <code>AsyncStorage</code>).
            </li>
            <li>
              <strong>No Mandatory Accounts or Login:</strong> You do not need
              an account, email address, phone number, or authentication
              credentials to use any feature in the application.
            </li>
            <li>
              <strong>No Financial Account Linking:</strong> Expentry does not
              connect to Plaid, bank APIs, credit card processors, or read SMS
              messages. You never enter banking credentials, passwords, or
              credit card numbers.
            </li>
            <li>
              <strong>Offline by Default:</strong> All analytics, cash flow
              hubs, category breakdowns, period filters, and search functions
              operate completely offline without an internet connection.
            </li>
          </ul>

          <Section>2. Device Permissions &amp; Transparent Usage</Section>
          <p>
            The application requests strictly the minimal permissions necessary
            to facilitate user-initiated features:
          </p>

          <Grid>
            <FeatureBox>
              <h4>📷 Camera (Optional)</h4>
              <p>
                Used exclusively if you choose to take a photo of physical
                purchase receipts to attach to a transaction note. Images remain
                strictly on your device.
              </p>
            </FeatureBox>

            <FeatureBox>
              <h4>💾 Local Storage &amp; Sharing</h4>
              <p>
                Used only when you explicitly tap &ldquo;Export CSV&rdquo; in
                the History tab to write and share your financial ledger file.
              </p>
            </FeatureBox>

            <FeatureBox>
              <h4>📳 Haptics &amp; Vibration</h4>
              <p>
                Provides subtle, tactile feedback when interacting with buttons,
                filter pills, and confirming income or expense records.
              </p>
            </FeatureBox>

            <FeatureBox>
              <h4>🛡️ Privacy Shield Mode</h4>
              <p>
                Enables one-tap masking of your total liquidity and balance
                numbers on the dashboard card to prevent shoulder-surfing in
                public.
              </p>
            </FeatureBox>
          </Grid>

          <Section>3. Data Retention, Export &amp; Full User Control</Section>
          <p>
            Because all data lives on your device, you possess total sovereignty
            over your financial history:
          </p>
          <ul>
            <li>
              <strong>Instant Ledger Export:</strong> You can export your full
              transaction ledger as a standard CSV file at any time via{" "}
              <strong>History &rarr; Export</strong>.
            </li>
            <li>
              <strong>Granular Deletion:</strong> You can delete any individual
              transaction directly from the ledger view.
            </li>
            <li>
              <strong>Complete Data Purge:</strong> You can permanently wipe all
              transactions, statistics, and customized settings with a single
              tap in <strong>Profile &rarr; Clear All Transactions</strong>.
            </li>
            <li>
              <strong>App Uninstallation:</strong> Uninstalling Expentry from
              your device or clearing app data in Android/iOS system settings
              permanently and irreversibly erases all local data.
            </li>
          </ul>

          <Section>4. Third-Party Services &amp; Advertising</Section>
          <p>
            Expentry does <strong>not</strong> include any third-party
            advertising SDKs, behavioral analytics trackers, profiling scripts,
            or data brokers. We do not sell, rent, monetize, or trade any user
            information with any external party.
          </p>

          <Section>5. Children&apos;s Privacy (COPPA &amp; GDPR)</Section>
          <p>
            Expentry does not collect personal data from anyone, including
            children under 13 (or under 16 in the European Union). The app
            complies fully with COPPA, GDPR, and international child privacy
            regulations.
          </p>

          <Section>6. Changes to this Policy</Section>
          <p>
            We may update our Privacy Policy periodically to reflect new app
            capabilities. Any modifications will be published on this page with
            an updated &ldquo;Last updated&rdquo; timestamp.
          </p>

          <Section>7. Contact &amp; Support</Section>
          <Card>
            If you have questions, inquiries, or feedback regarding this Privacy
            Policy or Expentry, please contact:
            <br />
            <br />
            <strong>Developer / Maintainer:</strong> Zayn Miraj / Nimions
            <br />
            <strong>Email:</strong>{" "}
            <a href="mailto:hello@nimions.com">hello@nimions.com</a>
            <br />
            <strong>Account &amp; Data Deletion Page:</strong>{" "}
            <a href="/expentry/delete-account">/expentry/delete-account</a>
          </Card>
        </Body>

        <Footer>
          &copy; 2026 Expentry - Track Your Expense. All rights reserved.
          Applicable to package <strong>com.zaynmiraj.Expentry</strong> (and
          previous package identifiers).
        </Footer>
      </Wrap>
    </Page>
  );
};

export default Expentry;
